import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Video } from '@/lib/api';
import { usePlaylistContext } from '@/contexts/PlaylistContext';

export function useVideoActions(videoDetails: Video) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingPlaylist, setIsCreatingPlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Utiliser le contexte pour les données en temps réel
  const { playlists, playlistVideos, favorites } = usePlaylistContext();
  
  // Vérifier si la vidéo est dans les favoris
  const isFavorite = favorites.includes(videoDetails.id);

  const toggleFavorite = async () => {
    if (!user) {
      toast({
        title: 'Authentification requise',
        description: 'Veuillez vous connecter pour ajouter aux favoris',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      if (isFavorite) {
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('video_id', videoDetails.id);
        
        if (error) throw error;
        
        // La mise à jour sera automatique via le hook realtime
        
        toast({
          title: 'Retiré des favoris',
          description: 'La vidéo a été retirée de vos favoris',
        });
      } else {
        // Sauvegarder la vidéo complète dans user_videos
        const { error: videoError } = await supabase
          .from('user_videos')
          .upsert({
            video_id: videoDetails.id,
            title: videoDetails.title,
            thumbnail: videoDetails.thumbnail,
            channel_name: videoDetails.channelName,
            channel_avatar: videoDetails.channelAvatar,
            channel_id: videoDetails.channelId,
            views: videoDetails.views,
            published: videoDetails.published,
            published_date: videoDetails.publishedDate,
            duration: videoDetails.duration,
            added_by_user_id: user.id,
          });

        if (videoError) {
          console.error('Error saving video details:', videoError);
        }

        const { error } = await supabase
          .from('favorites')
          .insert({
            user_id: user.id,
            video_id: videoDetails.id,
          });
        
        if (error) throw error;
        
        // La mise à jour sera automatique via le hook realtime
        
        toast({
          title: 'Ajouté aux favoris',
          description: 'La vidéo a été ajoutée à vos favoris',
        });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour les favoris',
        variant: 'destructive',
      });
    }
  };

  const togglePlaylistMembership = async (videoId: string, playlistId: string) => {
    if (!user) return;
    
    // Vérifier avec les données en temps réel si la vidéo est déjà dans la playlist
    const isInPlaylist = playlistVideos[playlistId]?.includes(videoId);
    
    try {
      setIsLoading(true);
      
      if (isInPlaylist) {
        // Retirer de la playlist
        const { error } = await supabase
          .from('playlist_items')
          .delete()
          .eq('playlist_id', playlistId)
          .eq('video_id', videoId)
          .eq('user_id', user.id);
        
        if (error) throw error;
        
        toast({
          title: 'Retirée de la playlist',
          description: 'La vidéo a été retirée de votre playlist',
        });
      } else {
        // Ajouter à la playlist
        // Sauvegarder la vidéo complète dans user_videos
        const { error: videoError } = await supabase
          .from('user_videos')
          .upsert({
            video_id: videoDetails.id,
            title: videoDetails.title,
            thumbnail: videoDetails.thumbnail,
            channel_name: videoDetails.channelName,
            channel_avatar: videoDetails.channelAvatar,
            channel_id: videoDetails.channelId,
            views: videoDetails.views,
            published: videoDetails.published,
            published_date: videoDetails.publishedDate,
            duration: videoDetails.duration,
            added_by_user_id: user.id,
          });

        if (videoError) {
          console.error('Error saving video details:', videoError);
        }
        
        const { error } = await supabase
          .from('playlist_items')
          .insert({
            playlist_id: playlistId,
            video_id: videoId,
            user_id: user.id,
          });
        
        if (error) throw error;
        
        toast({
          title: 'Ajoutée à la playlist',
          description: 'La vidéo a été ajoutée à votre playlist',
        });
      }
    } catch (error) {
      console.error('Error toggling playlist membership:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier la playlist',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addToPlaylist = async (videoId: string, playlistId: string) => {
    if (!user) return;
    
    // Vérifier avec les données en temps réel si la vidéo est déjà dans la playlist
    const isInPlaylist = playlistVideos[playlistId]?.includes(videoId);
    
    if (isInPlaylist) {
      toast({
        title: 'Déjà dans la playlist',
        description: 'Cette vidéo est déjà dans la playlist sélectionnée',
      });
      return;
    }
    
    try {
      setIsLoading(true);

      // Sauvegarder la vidéo complète dans user_videos
      const { error: videoError } = await supabase
        .from('user_videos')
        .upsert({
          video_id: videoDetails.id,
          title: videoDetails.title,
          thumbnail: videoDetails.thumbnail,
          channel_name: videoDetails.channelName,
          channel_avatar: videoDetails.channelAvatar,
          channel_id: videoDetails.channelId,
          views: videoDetails.views,
          published: videoDetails.published,
          published_date: videoDetails.publishedDate,
          duration: videoDetails.duration,
          added_by_user_id: user.id,
        });

      if (videoError) {
        console.error('Error saving video details:', videoError);
      }
      
      const { error } = await supabase
        .from('playlist_items')
        .insert({
          playlist_id: playlistId,
          video_id: videoId,
          user_id: user.id,
        });
      
      if (error) throw error;
      
      toast({
        title: 'Ajouté à la playlist',
        description: 'La vidéo a été ajoutée à votre playlist',
      });
      
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error adding to playlist:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'ajouter la vidéo à la playlist',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const createPlaylist = async (videoId: string) => {
    if (!user) return;
    
    if (!newPlaylistName.trim()) {
      toast({
        title: 'Nom requis',
        description: 'Veuillez entrer un nom pour votre playlist',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('playlists')
        .insert({
          name: newPlaylistName.trim(),
          user_id: user.id,
        })
        .select();
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        // Immediately add the video to the new playlist
        await addToPlaylist(videoId, data[0].id);
      }
      
      toast({
        title: 'Playlist créée',
        description: `"${newPlaylistName}" a été créée`,
      });
      
      setNewPlaylistName('');
      setIsCreatingPlaylist(false);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error creating playlist:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de créer la playlist',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    playlists,
    playlistVideos,
    isFavorite,
    isDialogOpen,
    setIsDialogOpen,
    isLoading,
    isCreatingPlaylist,
    setIsCreatingPlaylist,
    newPlaylistName,
    setNewPlaylistName,
    toggleFavorite,
    addToPlaylist,
    togglePlaylistMembership,
    createPlaylist
  };
}